import { IonIcon, IonInput, IonTextarea, type TextareaCustomEvent } from '@ionic/react';
import { alertCircleOutline } from 'ionicons/icons';
import type { ReactNode } from 'react';

interface BaseFieldProps {
  label: string;
  // biome-ignore lint/suspicious/noExplicitAny: ionicons icon type
  icon?: any;
  hint?: string;
  error?: string | null;
  optional?: boolean;
  children: ReactNode;
  id?: string;
}

export function FieldWrap({
  label,
  icon,
  hint,
  error,
  optional,
  children,
  id,
}: BaseFieldProps): JSX.Element {
  return (
    <div className="rx-field">
      <label
        htmlFor={id}
        className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-overline text-ink-muted"
      >
        {icon && <IonIcon icon={icon} className="text-[13px] text-ink-subtle" />}
        {label}
        {optional && (
          <span className="font-mono text-[10px] normal-case tracking-normal text-ink-subtle">
            (opcional)
          </span>
        )}
      </label>
      {children}
      {error ? (
        <p className="flex items-center gap-1 text-[12px] text-negative mt-1">
          <IonIcon icon={alertCircleOutline} className="text-[13px]" />
          {error}
        </p>
      ) : hint ? (
        <p className="text-[12px] text-ink-subtle mt-1">{hint}</p>
      ) : null}
    </div>
  );
}

interface MaskedInputProps {
  id?: string;
  value: string;
  onChange(value: string): void;
  placeholder?: string;
  inputmode?: 'text' | 'numeric' | 'decimal' | 'email' | 'tel';
  autocomplete?: 'on' | 'off' | 'email' | 'tel' | 'new-password';
  type?: 'text' | 'email' | 'tel' | 'password';
  disabled?: boolean;
  maxLength?: number;
}

export function MaskedInput({
  id,
  value,
  onChange,
  placeholder,
  inputmode = 'text',
  autocomplete = 'off',
  type = 'text',
  disabled = false,
  maxLength,
}: MaskedInputProps): JSX.Element {
  return (
    <IonInput
      id={id}
      type={type}
      inputmode={inputmode}
      autocomplete={autocomplete}
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      maxlength={maxLength}
      onIonInput={(event) => onChange(event.detail.value ?? '')}
    />
  );
}

interface TextAreaFieldProps {
  id?: string;
  value: string;
  onChange(value: string): void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
}

export function TextAreaField({
  id,
  value,
  onChange,
  placeholder,
  rows = 4,
  maxLength,
}: TextAreaFieldProps): JSX.Element {
  const handleInput = (event: TextareaCustomEvent): void => {
    onChange(event.detail.value ?? '');
  };
  return (
    <IonTextarea
      id={id}
      value={value}
      placeholder={placeholder}
      rows={rows}
      autoGrow={false}
      maxlength={maxLength}
      onIonInput={handleInput}
      className="rx-textarea"
    />
  );
}
